/**
 * ReportsMenuButton — przycisk „Zestawienia" z listą raportów, jak w desktopowym Handlu.
 *
 * Raport, którego jeszcze nie ma, zostaje na liście jako pozycja WYŁĄCZONA. To informacja
 * o zakresie, nie niedoróbka: ukrycie sprawiłoby, że menu „zmienia się" po dowiezieniu, a
 * użytkownik Symfonii spodziewa się tu konkretnego zestawu pozycji.
 *
 * Panel renderujemy przez PORTAL, kotwicząc go do przycisku — belka akcji potrafi trafić do
 * kontenera z przewijaniem, a wtedy panel pozycjonowany w drzewie zostałby przycięty.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet } from 'lucide-react';

import { Button } from '@/components/Button';

import { ToolbarButton } from './HmListShell';

export interface ReportMenuItem {
  key: string;
  label: string;
  /** Brak akcji = pozycja wyłączona („wkrótce"). */
  onSelect?: () => void;
}

export function ReportsMenuButton({
  items,
  testIdPrefix = 'hm-reports',
}: {
  items: ReportMenuItem[];
  testIdPrefix?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      setAnchor({ top: rect.bottom + 4, left: rect.left });
    };
    place();
    // Panel jest przypięty do okna, więc po przewinięciu albo zmianie rozmiaru odjechałby od
    // przycisku — przeliczamy, zamiast zostawiać go wiszącego w powietrzu.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      // Klik poza zamyka — panel żyje w portalu, więc trzeba sprawdzić OBA drzewa.
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  return (
    // Kotwica MUSI mieć własne pudełko. Przy `display: contents` element nie ma prostokąta,
    // `getBoundingClientRect()` oddaje zera i panel ląduje w lewym górnym rogu ekranu zamiast
    // pod przyciskiem.
    <div ref={triggerRef} className="inline-flex">
      <ToolbarButton
        icon={<FileSpreadsheet className="h-4 w-4" />}
        label={t('tenant.handelMagazyn.toolbar.reports')}
        enabled
        onClick={() => setOpen((v) => !v)}
        testId={`${testIdPrefix}-btn`}
        ariaExpanded={open}
      />
      {open &&
        anchor &&
        createPortal(
          <div
            ref={panelRef}
            role="menu"
            style={{ position: 'fixed', top: anchor.top, left: anchor.left }}
            className="z-[100] min-w-[260px] rounded-lg border border-border-secondary bg-bg-elevated py-1 shadow-lg"
            data-testid={`${testIdPrefix}-menu`}
          >
            <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
              {t('tenant.handelMagazyn.reports.menuTitle')}
            </p>
            {items.map((item) => (
              <Button
                key={item.key}
                variant="ghost"
                size="sm"
                role="menuitem"
                disabled={!item.onSelect}
                title={item.onSelect ? item.label : t('tenant.handelMagazyn.toolbar.soon')}
                onClick={() => {
                  setOpen(false);
                  item.onSelect?.();
                }}
                className="w-full justify-start rounded-none px-3 text-sm font-normal"
                data-testid={`${testIdPrefix}-item-${item.key}`}
              >
                {item.label}
                {!item.onSelect && (
                  <span className="ml-auto rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] text-text-tertiary">
                    {t('tenant.handelMagazyn.toolbar.soon')}
                  </span>
                )}
              </Button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
